import React, { useRef, useState } from 'react';
import {
  Camera,
  Upload,
  Trash2,
  Edit2,
  Check,
  X,
  AlertTriangle,
  Plus,
  Image as ImageIcon,
  Star,
  CheckCircle2,
  HelpCircle,
  Eye,
} from 'lucide-react';

export interface CompanionPhotoGalleryProps {
  avatarUrl: string;
  gallery: string[];
  userName?: string;
  isIdVerified?: boolean;
  onSetPrimaryAvatar?: (url: string) => void;
  onAddPhotos?: (urls: string[]) => void;
  onDeletePhoto?: (url: string) => void;
  onUpdateGallery?: (newGallery: string[], newAvatarUrl?: string) => void;
  onSelectPresetAvatar?: (url: string) => void;
  onResetPhoto?: () => void;
  onShowToast: (msg: string, type?: 'success' | 'heart' | 'info') => void;
}

export const CompanionPhotoGallery: React.FC<CompanionPhotoGalleryProps> = ({
  avatarUrl,
  gallery,
  userName = 'Companion Host',
  isIdVerified = true,
  onSetPrimaryAvatar,
  onAddPhotos,
  onDeletePhoto,
  onUpdateGallery,
  onResetPhoto,
  onShowToast,
}) => {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const normalizeGalleryList = (gal?: string[], fallbackAvatar?: string): string[] => {
    if (Array.isArray(gal)) {
      return Array.from(new Set(gal.filter(Boolean))).slice(0, 6);
    }
    return fallbackAvatar ? [fallbackAvatar] : [];
  };

  const initialPhotos = normalizeGalleryList(gallery, avatarUrl);

  const [photos, setPhotos] = useState<string[]>(initialPhotos);
  const [selectedAvatar, setSelectedAvatar] = useState<string>(() => {
    if (avatarUrl && initialPhotos.includes(avatarUrl)) return avatarUrl;
    return initialPhotos[0] || avatarUrl || '';
  });

  // Synchronize internal state when props change externally
  React.useEffect(() => {
    const updated = normalizeGalleryList(gallery, avatarUrl);
    setPhotos(updated);
    if (avatarUrl && updated.includes(avatarUrl)) {
      setSelectedAvatar(avatarUrl);
    } else {
      setSelectedAvatar(updated[0] || avatarUrl || '');
    }
  }, [avatarUrl, gallery]);

  // Edit State
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [tempEditPhoto, setTempEditPhoto] = useState<string | null>(null);

  // Delete State
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  // Lightbox Preview State
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);

  // Helper to commit changes to parent and storage
  const commitGalleryUpdate = (newPhotosList: string[], explicitAvatarUrl?: string) => {
    const validPhotos = Array.from(new Set(newPhotosList.filter(Boolean))).slice(0, 6);
    setPhotos(validPhotos);

    let newPrimaryAvatar: string;
    if (explicitAvatarUrl !== undefined) {
      newPrimaryAvatar = explicitAvatarUrl;
    } else if (selectedAvatar && validPhotos.includes(selectedAvatar)) {
      newPrimaryAvatar = selectedAvatar;
    } else {
      newPrimaryAvatar = validPhotos[0] || '';
    }
    setSelectedAvatar(newPrimaryAvatar);

    if (onUpdateGallery) {
      onUpdateGallery(validPhotos, newPrimaryAvatar);
    } else {
      if (onAddPhotos) onAddPhotos(validPhotos);
      if (onSetPrimaryAvatar && newPrimaryAvatar !== undefined) onSetPrimaryAvatar(newPrimaryAvatar);
    }

    // Direct localStorage backup
    try {
      const stored = localStorage.getItem('pairup_user_profile_data');
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.gallery = validPhotos;
        parsed.avatarUrl = newPrimaryAvatar;
        localStorage.setItem('pairup_user_profile_data', JSON.stringify(parsed));
      }
    } catch (e) {
      console.error('Failed to save gallery in local storage', e);
    }
  };

  // 1. Upload Handler (+ Upload Photo)
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (photos.length >= 6) {
      onShowToast('Maximum 6 photos allowed. Please delete or edit existing photos.', 'info');
      if (e.target) e.target.value = '';
      return;
    }

    const availableSlots = 6 - photos.length;
    const filesToProcess = Array.from(files).slice(0, availableSlots);

    const validFiles = filesToProcess.filter((file) => {
      const isImage = file.type.startsWith('image/');
      const isValidExt = /\.(jpe?g|png|webp)$/i.test(file.name);
      return isImage || isValidExt;
    });

    if (validFiles.length === 0) {
      onShowToast('Please upload high-quality JPG, PNG, or WebP images.', 'info');
      if (e.target) e.target.value = '';
      return;
    }

    let loadedCount = 0;
    const newPhotosData: string[] = [];

    validFiles.forEach((file) => {
      if (file.size > 8 * 1024 * 1024) {
        onShowToast(`File ${file.name} is larger than 8MB. Please select a smaller file.`, 'info');
        loadedCount++;
        if (loadedCount === validFiles.length && newPhotosData.length > 0) {
          finalizeUpload(newPhotosData);
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        if (result) {
          newPhotosData.push(result);
        }
        loadedCount++;
        if (loadedCount === validFiles.length) {
          finalizeUpload(newPhotosData);
        }
      };
      reader.onerror = () => {
        loadedCount++;
        if (loadedCount === validFiles.length && newPhotosData.length > 0) {
          finalizeUpload(newPhotosData);
        }
      };
      reader.readAsDataURL(file);
    });

    const finalizeUpload = (newItems: string[]) => {
      const combined = Array.from(new Set([...photos, ...newItems])).slice(0, 6);
      const preservedAvatar =
        selectedAvatar && combined.includes(selectedAvatar) ? selectedAvatar : (combined[0] || '');
      commitGalleryUpdate(combined, preservedAvatar);
      onShowToast(`Added ${newItems.length} photo${newItems.length > 1 ? 's' : ''} to your gallery! 📸`, 'success');
      if (e.target) e.target.value = '';
    };
  };

  // 2. Edit Action: Select replacement file
  const handleTriggerEdit = (index: number) => {
    setEditingIndex(index);
    setTempEditPhoto(null);
    if (replaceInputRef.current) {
      replaceInputRef.current.value = '';
      replaceInputRef.current.click();
    }
  };

  const handleReplacementFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      // If user cancelled file selector without picking, keep editing index or reset
      return;
    }

    const isValidFormat = file.type.startsWith('image/') || /\.(jpe?g|png|webp)$/i.test(file.name);
    if (!isValidFormat) {
      onShowToast('Please select a JPG, PNG, or WebP image.', 'info');
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      onShowToast('File exceeds 8MB limit. Please choose a smaller image.', 'info');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      if (dataUrl) {
        setTempEditPhoto(dataUrl);
      }
    };
    reader.readAsDataURL(file);
  };

  // Save Changes for current edited photo
  const handleSaveChanges = () => {
    if (editingIndex === null || !tempEditPhoto) {
      setEditingIndex(null);
      setTempEditPhoto(null);
      return;
    }

    const previousPhotoAtSlot = photos[editingIndex];
    const updated = [...photos];
    updated[editingIndex] = tempEditPhoto;
    const nextAvatar =
      previousPhotoAtSlot === selectedAvatar || !updated.includes(selectedAvatar)
        ? tempEditPhoto
        : selectedAvatar;

    commitGalleryUpdate(updated, nextAvatar);

    onShowToast(`Photo ${editingIndex + 1} updated successfully! ✨`, 'success');
    setEditingIndex(null);
    setTempEditPhoto(null);
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingIndex(null);
    setTempEditPhoto(null);
  };

  // 3. Delete Action: Confirm & remove
  const handleTriggerDelete = (index: number) => {
    setDeletingIndex(index);
  };

  const handleConfirmDelete = () => {
    if (deletingIndex === null) return;

    const removedUrl = photos[deletingIndex];
    const remaining = photos.filter((_, idx) => idx !== deletingIndex);
    const nextAvatar =
      removedUrl === selectedAvatar || !remaining.includes(selectedAvatar)
        ? remaining[0] || ''
        : selectedAvatar;

    commitGalleryUpdate(remaining, nextAvatar);
    if (onDeletePhoto) {
      onDeletePhoto(removedUrl);
    }

    onShowToast('Photo removed from your gallery.', 'info');
    setDeletingIndex(null);
  };

  const handleCancelDelete = () => {
    setDeletingIndex(null);
  };

  // 4. Select ANY gallery photo and set as Profile Photo (can be changed at any time)
  const handleMakePrimary = (index: number) => {
    const target = photos[index];
    if (!target) return;
    const activeAvatar = selectedAvatar && photos.includes(selectedAvatar) ? selectedAvatar : (photos[0] || '');
    if (target === activeAvatar && selectedAvatar === target) return;

    setSelectedAvatar(target);
    commitGalleryUpdate(photos, target);
    if (onSetPrimaryAvatar) onSetPrimaryAvatar(target);
    onShowToast('Set as your primary profile photo! ⭐', 'success');
  };

  // Generate 6 display slots (filled or empty)
  const totalSlots = 6;
  const activeProfilePhoto =
    selectedAvatar && photos.includes(selectedAvatar) ? selectedAvatar : (photos[0] || '');
  const slots = Array.from({ length: totalSlots }).map((_, index) => {
    const photo = photos[index] || null;
    return { index, photo };
  });

  return (
    <div id="companion-photo-gallery" className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 space-y-6">
      {/* Hidden File Inputs */}
      <input
        ref={uploadInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileUpload}
        className="hidden"
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleReplacementFileSelected}
        className="hidden"
      />

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xl">📸</span>
            <h3 className="text-lg sm:text-xl font-black text-slate-900 tracking-tight">
              Photo Gallery
            </h3>
            {isIdVerified && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Verified Host
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm text-slate-600">
            Add up to 6 high-quality photos for client viewing.
          </p>
        </div>

        {/* Counter Badge */}
        <div className="flex items-center gap-2">
          <span
            className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
              photos.length === 6
                ? 'bg-amber-50 text-amber-800 border-amber-300'
                : 'bg-slate-100 text-slate-700 border-slate-200'
            }`}
          >
            {photos.length} / 6 photos
          </span>
        </div>
      </div>

      {/* Guidelines / Specifications Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-slate-600 bg-slate-50 p-3 rounded-2xl border border-slate-200/80">
        <div className="flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span>Maximum 6 high-resolution photos</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          <span>Supports high-quality JPG, PNG, and WebP</span>
        </div>
      </div>

      {/* 6-Photo Recommended Grid:
          [ Photo 1 ] [ Photo 2 ] [ Photo 3 ]
          [  Edit   ] [  Edit   ] [  Edit   ]
          [ Delete  ] [ Delete  ] [ Delete  ]

          [ Photo 4 ] [ Photo 5 ] [ Photo 6 ]
          [  Edit   ] [  Edit   ] [  Edit   ]
          [ Delete  ] [ Delete  ] [ Delete  ]
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
        {slots.map(({ index, photo }) => {
          const isBeingEdited = editingIndex === index;
          const displayImage = isBeingEdited && tempEditPhoto ? tempEditPhoto : photo;
          const isPrimary = Boolean(photo) && photo === activeProfilePhoto;

          if (!photo && !isBeingEdited) {
            // Empty Slot card
            return (
              <div
                key={`empty-slot-${index}`}
                id={`photo-slot-${index + 1}`}
                onClick={() => {
                  if (photos.length < 6) {
                    uploadInputRef.current?.click();
                  }
                }}
                className={`group border-2 border-dashed rounded-2xl p-4 flex flex-col items-center justify-center text-center transition-all min-h-[220px] ${
                  photos.length < 6
                    ? 'border-slate-200 hover:border-pink-400 bg-slate-50/60 hover:bg-pink-50/30 cursor-pointer'
                    : 'border-slate-200 bg-slate-50/40 opacity-60 cursor-not-allowed'
                }`}
              >
                <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 group-hover:text-pink-600 group-hover:border-pink-300 transition-colors shadow-2xs mb-2.5">
                  <Plus className="w-6 h-6" />
                </div>
                <span className="text-xs font-bold text-slate-700 block">
                  Photo {index + 1}
                </span>
                <span className="text-[11px] text-slate-400 mt-0.5">
                  {photos.length < 6 ? 'Click to upload' : 'Empty slot'}
                </span>
              </div>
            );
          }

          // Filled or Editing Photo Card
          return (
            <div
              key={`photo-slot-${index}`}
              id={`photo-slot-${index + 1}`}
              className={`bg-white rounded-2xl border transition-all overflow-hidden flex flex-col ${
                isBeingEdited
                  ? 'border-pink-500 ring-2 ring-pink-500/20 shadow-md'
                  : 'border-slate-200 hover:border-slate-300 shadow-2xs'
              }`}
            >
              {/* Photo Display Window */}
              <div className="relative aspect-[4/3] sm:aspect-square bg-slate-100 overflow-hidden group">
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt={`Gallery Photo ${index + 1}`}
                    className="w-full h-full object-cover object-center group-hover:scale-102 transition-transform duration-200 cursor-pointer"
                    onClick={() => setPreviewPhoto(displayImage)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <ImageIcon className="w-10 h-10" />
                  </div>
                )}

                {/* Photo Slot Badge */}
                <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md bg-slate-900/80 text-white text-[10px] font-black tracking-wide backdrop-blur-xs">
                  Photo {index + 1}
                </span>

                {/* Primary Profile Star Badge */}
                {isPrimary && (
                  <span className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-md bg-emerald-600 text-white text-[10px] font-black flex items-center gap-1 shadow-xs">
                    <Star className="w-3 h-3 fill-white" /> Primary
                  </span>
                )}

                {/* Preview expansion button */}
                {displayImage && !isBeingEdited && (
                  <button
                    type="button"
                    onClick={() => setPreviewPhoto(displayImage)}
                    className="absolute bottom-2.5 right-2.5 p-1.5 rounded-lg bg-slate-900/70 hover:bg-slate-900 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="View Full Size"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                )}

                {/* Pending Edit Overlay Notice */}
                {isBeingEdited && tempEditPhoto && (
                  <div className="absolute inset-x-0 bottom-0 bg-pink-600/90 text-white text-[10px] font-bold py-1 text-center backdrop-blur-xs">
                    Preview of new photo
                  </div>
                )}
              </div>

              {/* Action Buttons Underneath Photo */}
              <div className="p-3 bg-white border-t border-slate-100 space-y-2 flex-1 flex flex-col justify-end">
                {isBeingEdited ? (
                  /* Editing Mode: Save Changes & Cancel */
                  <div className="space-y-1.5 animate-in fade-in duration-150">
                    {tempEditPhoto ? (
                      <button
                        type="button"
                        id={`save-photo-${index + 1}`}
                        onClick={handleSaveChanges}
                        className="w-full py-2 px-3 bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <Check className="w-3.5 h-3.5" />
                        <span>Save Changes</span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => replaceInputRef.current?.click()}
                        className="w-full py-2 px-3 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        <span>Select New Photo</span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="w-full py-1.5 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-xl transition-colors flex items-center justify-center gap-1 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                      <span>Cancel</span>
                    </button>
                  </div>
                ) : (
                  /* Standard Mode: Edit and Delete */
                  <div className="space-y-1.5">
                    {/* Primary Photo Quick Set for non-primary */}
                    {!isPrimary && (
                      <button
                        type="button"
                        id={`set-profile-photo-${index + 1}`}
                        onClick={() => handleMakePrimary(index)}
                        className="w-full py-1 text-[11px] font-bold text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors flex items-center justify-center gap-1 cursor-pointer"
                        title="Set this photo as your main display avatar"
                      >
                        <Star className="w-3 h-3 text-emerald-600" />
                        <span>Set as Profile Photo</span>
                      </button>
                    )}

                    {/* Edit Button */}
                    <button
                      type="button"
                      id={`edit-photo-${index + 1}`}
                      onClick={() => handleTriggerEdit(index)}
                      className="w-full py-2 px-3 bg-slate-50 hover:bg-slate-100 text-slate-800 hover:text-slate-900 text-xs font-bold rounded-xl border border-slate-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-slate-600" />
                      <span>Edit</span>
                    </button>

                    {/* Delete Button */}
                    <button
                      type="button"
                      id={`delete-photo-${index + 1}`}
                      onClick={() => handleTriggerDelete(index)}
                      className="w-full py-1.5 px-3 bg-white hover:bg-rose-50 text-slate-600 hover:text-rose-700 text-xs font-semibold rounded-xl border border-slate-200 hover:border-rose-200 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Center Controls */}
      <div className="pt-4 border-t border-slate-100 flex flex-col items-center justify-center gap-2">
        <button
          type="button"
          id="btn-upload-photo"
          disabled={photos.length >= 6}
          onClick={() => uploadInputRef.current?.click()}
          className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs sm:text-sm rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          <span>+ Upload Photo</span>
        </button>

        <p className="text-xs font-semibold text-slate-500">
          {photos.length} / 6 photos
        </p>

        {photos.length >= 6 && (
          <p className="text-[11px] text-amber-700 font-medium bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
            Gallery full (6/6 photos). Click Edit or Delete on any photo to make changes.
          </p>
        )}
      </div>

      {/* =========================================================================
          DELETE CONFIRMATION MODAL
          "Photo সরাতে Delete → confirmation → remove"
         ========================================================================= */}
      {deletingIndex !== null && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div
            id="delete-photo-confirmation-modal"
            className="bg-white rounded-3xl p-6 max-w-sm w-full border border-slate-200 shadow-2xl space-y-4 animate-in zoom-in-95 duration-150"
          >
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6" />
            </div>

            <div className="text-center space-y-1">
              <h4 className="text-base font-black text-slate-900">Delete Photo?</h4>
              <p className="text-xs text-slate-600">
                Are you sure you want to remove <strong>Photo {deletingIndex + 1}</strong> from your public gallery?
              </p>
            </div>

            {/* Thumbnail preview of photo to be deleted */}
            {photos[deletingIndex] && (
              <div className="w-24 h-24 rounded-2xl overflow-hidden mx-auto border-2 border-slate-200 shadow-xs">
                <img
                  src={photos[deletingIndex]}
                  alt="Deleting preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                id="cancel-delete-btn"
                onClick={handleCancelDelete}
                className="w-full py-2 px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                id="confirm-delete-btn"
                onClick={handleConfirmDelete}
                className="w-full py-2 px-3 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          LIGHTBOX PHOTO PREVIEW MODAL
         ========================================================================= */}
      {previewPhoto && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setPreviewPhoto(null)}
        >
          <div
            className="relative max-w-2xl max-h-[85vh] bg-white rounded-3xl overflow-hidden shadow-2xl p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewPhoto(null)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-slate-900/70 hover:bg-slate-900 text-white transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={previewPhoto}
              alt="High-resolution gallery preview"
              className="w-full max-h-[80vh] object-contain rounded-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};
